import React from "react";
import { Callout, Code, CTA, Lead, P, Section } from "../../../../blog/components";

export const title = "Cómo observamos seis CLI de agentes sin envolver sus procesos";
export const excerpt =
  "Claude Code, Codex, Cursor, Grok, OpenCode y Pi informan su actividad de formas distintas. Convertimos sus hooks nativos en un ciclo de vida común.";
export const heroAlt = "Una gran llanura verde vista desde una ladera rocosa";
export const readingTime = "4 min de lectura";

export default function Post() {
  return (
    <>
      <Lead>
        Un terminal sabe si existe un proceso. No sabe si un agente está trabajando, esperando una
        aprobación o si ya terminó su turno. Con seis agentes abiertos a la vez, esa diferencia importa.
      </Lead>

      <P>
        Cate funciona con Claude Code, Codex, Cursor, Grok, OpenCode y Pi. Queríamos que cada terminal
        mostrara el mismo ciclo de vida sin cambiar la forma de iniciar ninguna de estas herramientas:
      </P>

      <Code>{`trabajando
  -> esperando permiso o entrada
  -> terminado`}</Code>

      <P>
        Analizar el texto del terminal no era una opción fiable. Los indicadores cambian, el texto se
        redibuja y un proceso silencioso puede estar pensando, ejecutando una herramienta o bloqueado.
        El árbol de procesos solo indica si la CLI sigue viva.
      </P>

      <Section title="Un flujo común para seis APIs de hooks">
        <P>
          Cada CLI ofrece un punto de integración estructurado, pero usa nombres distintos. Claude Code
          tiene eventos como <code>UserPromptSubmit</code>, <code>Notification</code> y <code>Stop</code>.
          OpenCode informa <code>session.status</code>, <code>permission.asked</code> y
          <code>session.idle</code>. Pi ofrece una API de extensiones dentro del proceso.
        </P>

        <P>Cate los traduce a seis eventos internos:</P>

        <Code>{`session-start
turn-start
permission-wait
turn-resume
turn-end
session-end`}</Code>

        <P>
          Un pequeño puente envía cada evento a un endpoint local del runtime de Cate. El ID del
          terminal permite encontrar el panel correcto y cada terminal recibe su propio token. En una
          máquina remota, el endpoint se ejecuta allí mismo.
        </P>

        <P>
          Cate no envuelve el proceso del agente. Ejecutar <code>codex</code> sigue iniciando Codex
          directamente. El puente solo aparece cuando la CLI llama a su hook nativo. Si la instalación
          del hook falla, Cate abre un shell normal.
        </P>

        <Callout>
          Los adaptadores no fingen que todas las CLI ofrecen la misma información. Cursor no tiene un
          evento específico para permisos, así que Cate no intenta adivinar. Su API actual no permite
          distinguir un comando a punto de ejecutarse de uno bloqueado esperando aprobación.
        </Callout>
      </Section>

      <Section title="El estado del turno no es el estado del proceso">
        <P>
          Un evento <code>turn-end</code> significa que el agente espera el siguiente prompt. No
          significa que el proceso haya terminado. Los cierres manuales y los fallos se detectan por
          separado.
        </P>

        <P>
          El primer hook autenticado le da a Cate un proceso que seguir. En los hooks que ejecutan un
          comando, el runtime recorre sus procesos padre hasta encontrar el agente. Los plugins internos
          pueden enviar su PID directamente. Una comprobación ligera detecta cuándo desaparece y marca
          el terminal como terminado.
        </P>
      </Section>

      <Section title="Un ID de sesión necesita una prueba">
        <P>
          Los adaptadores también recogen el ID de sesión. Cate lo guarda con el terminal y usa el
          comando de reanudación de cada CLI tras reiniciar la aplicación. Claude Code puede anunciar
          una sesión antes de que exista su transcripción, así que esperamos al primer evento del turno
          antes de guardarla.
        </P>

        <P>
          La restauración se intenta una sola vez. Cate escribe el comando de reanudación en un shell
          nuevo y borra la marca guardada. Si funciona, los nuevos hooks vuelven a crearla. Si el ID está
          obsoleto, la CLI muestra su error normal y el siguiente reinicio abre un shell vacío.
        </P>
      </Section>

      <Section title="Las notificaciones siguen cambios de estado">
        <P>
          Un turno con muchas herramientas produce muchos eventos. Por eso las notificaciones dependen
          de una transición, no de cada hook. El cambio de trabajando a esperando puede mostrar el
          comando bloqueado. Los eventos repetidos no hacen nada y un clic abre el terminal exacto.
        </P>

        <P>
          El modelo público sigue siendo pequeño: trabajando, esperando, terminado. La complejidad queda
          debajo, donde deben coincidir seis APIs distintas, la vida del proceso y las sesiones
          reanudables.
        </P>
      </Section>

      <CTA label="Ver el código de Cate" href="https://github.com/0-AI-UG/cate" />
    </>
  );
}
